import React, { useState, useEffect } from 'react';
import { Save, Settings, Globe, Sun, Moon, Image, Navigation } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useSite } from '../../contexts/SiteContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminSiteSettings() {
  const { settings, updateSettings } = useSite();
  const [formData, setFormData] = useState({
    site_name: '',
    site_tagline: '',
    site_description: '',
    default_theme: 'light',
    seo_image: '',
    contact_email: '',
    stepper_include_hidden: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData({
      site_name: settings.site_name || '',
      site_tagline: settings.site_tagline || '',
      site_description: settings.site_description || '',
      default_theme: settings.default_theme || 'light',
      seo_image: settings.seo_image || '',
      contact_email: settings.contact_email || '',
      stepper_include_hidden: settings.stepper_include_hidden !== false
    });
  }, [settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateSettings({
        ...settings,
        ...formData,
        gmail_user: settings.gmail_user || '',
        gmail_app_password: ''
      });
      toast.success('Site settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-2xl" data-testid="admin-site-settings">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Site Settings</h1>
        <p className="text-muted-foreground">Configure your site name, description, and appearance</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Site Information */}
        <div className="border border-border rounded-lg p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Settings className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Site Information</h2>
          </div>

          <div className="space-y-2">
            <Label htmlFor="site_name">Site Name</Label>
            <Input
              id="site_name"
              name="site_name"
              value={formData.site_name}
              onChange={handleChange}
              placeholder="My Awesome Site"
              data-testid="settings-site-name"
            />
            <p className="text-xs text-muted-foreground">
              Displayed in the header, footer, and browser tab
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="site_tagline">Tagline</Label>
            <Input
              id="site_tagline"
              name="site_tagline"
              value={formData.site_tagline}
              onChange={handleChange}
              placeholder="Work, Play, and Everything Else"
              data-testid="settings-site-tagline"
            />
            <p className="text-xs text-muted-foreground">
              Short tagline shown below the site name
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_email">Contact Email</Label>
            <Input
              id="contact_email"
              name="contact_email"
              type="email"
              value={formData.contact_email}
              onChange={handleChange}
              placeholder="contact@example.com"
              data-testid="settings-contact-email"
            />
            <p className="text-xs text-muted-foreground">
              Shown on the contact page and receives form submissions
            </p>
          </div>
        </div>

        {/* Appearance */}
        <div className="border border-border rounded-lg p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Sun className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Appearance</h2>
          </div>

          <div className="space-y-2">
            <Label>Default Theme</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.default_theme === 'light' ? 'default' : 'outline'}
                onClick={() => setFormData(prev => ({ ...prev, default_theme: 'light' }))}
                data-testid="theme-light-btn"
              >
                <Sun className="mr-2 h-4 w-4" />
                Light
              </Button>
              <Button
                type="button"
                variant={formData.default_theme === 'dark' ? 'default' : 'outline'}
                onClick={() => setFormData(prev => ({ ...prev, default_theme: 'dark' }))}
                data-testid="theme-dark-btn"
              >
                <Moon className="mr-2 h-4 w-4" />
                Dark
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Default theme for first-time visitors. Users can still toggle manually.
            </p>
          </div>
        </div>

        {/* SEO */}
        <div className="border border-border rounded-lg p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Globe className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">SEO</h2>
          </div>

          <div className="space-y-2">
            <Label htmlFor="site_description">Site Description</Label>
            <Textarea
              id="site_description"
              name="site_description"
              value={formData.site_description}
              onChange={handleChange}
              placeholder="A brief description of your site for search engines..."
              rows={3}
              data-testid="settings-site-description"
            />
            <p className="text-xs text-muted-foreground">
              Used as the default meta description for search engines and social sharing
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="seo_image">Default Social Image URL</Label>
            <Input
              id="seo_image"
              name="seo_image"
              value={formData.seo_image}
              onChange={handleChange}
              placeholder="https://example.com/og-image.jpg"
              data-testid="settings-seo-image"
            />
            <p className="text-xs text-muted-foreground">
              Default image shown when your site is shared on social media (og:image). Use a full URL.
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="border border-border rounded-lg p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Navigation className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Navigation</h2>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="stepper_include_hidden" className="cursor-pointer">
                Include hidden pages in Previous / Next
              </Label>
              <p className="text-xs text-muted-foreground">
                The Previous/Next stepper steps through pages that share a Menu Group and
                Sub-section. When on, pages hidden from the main menu are still part of the
                sequence (useful for keeping sub-pages out of the top nav). Turn off to step
                only through pages shown in the menu.
              </p>
            </div>
            <Switch
              id="stepper_include_hidden"
              checked={formData.stepper_include_hidden}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, stepper_include_hidden: checked }))}
              data-testid="settings-stepper-include-hidden"
            />
          </div>
        </div>

        <Button type="submit" disabled={saving} data-testid="save-site-settings-btn">
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </form>
    </div>
  );
}
